'use client';

import generateColorShades from '@/shared/lib/gen-color-shades';
import { ChakraProvider, createSystem, defaultConfig } from '@chakra-ui/react';
import '@smastrom/react-rating/styles.css';
import { useMemo } from 'react';

const color = '#152a45';

const COLORS = {
  dark: {
    DEFAULT: { value: '#101010' },
    text: { value: '#1E1E1E' },
    cardTitle: { value: '#101828' },
  },
  light: {
    DEFAULT: { value: '#ffffff' },
    200: { value: '#faf7f7' },
    300: { value: '#F3F3F3' },
    400: { value: '#EDEDED' },
    500: { value: '#F3F4F9' },
  },
  gray: {
    DEFAULT: { value: '#98A2B3' },
    500: { value: '#CDCDCD' },
    600: { value: '#676767' },
    700: { value: '#667085' },
    800: { value: '#475467' },
  },
  link: {
    DEFAULT: { value: '#4E60FF' },
  },
};

type Props = {
  children: React.ReactNode;
};

const ChakraUIProvider: React.FC<Props> = ({ children }) => {
  const shades = generateColorShades(color);

  const tokenColors = useMemo(() => {
    return {
      background: {
        card: { value: '#fff' },
        DEFAULT: { value: '#F3F3F3' },
      },
      primary: {
        ...shades,
        DEFAULT: { value: color },
        hover: { value: color },
        text: { value: '#FFFFFF' },
      },
      product_button: {
        DEFAULT: { value: '#F3F3F3' },
        hover: { value: '#FAF7F7' },
        text: { value: '#475467' },
      },
      text: {
        DEFAULT: { value: '#09090b' },
        muted: { value: '#676767' },
      },
      promo: { value: '#16A34A' },
      price: { value: color },
    };
  }, [color]);

  const system = createSystem(defaultConfig, {
    theme: {
      tokens: {
        colors: {
          ...tokenColors,
          ...COLORS,
          brand: {
            solid: { value: '{colors.primary.500}' },
            contrast: { value: '{colors.primary.100}' },
            fg: { value: '{colors.primary.400}' },
            muted: { value: '{colors.primary.100}' },
            subtle: { value: '{colors.primary.50}' },
            emphasized: { value: '{colors.primary.300}' },
            focusRing: { value: '{colors.primary.500}' },
          },
        },
      },
      semanticTokens: {
        colors: {
          semanticTokens: {
            colors: {
              bg: {
                card: { value: '{colors.background.card}' },
                default: { value: '{colors.background}' },
              },
              text: {
                default: { value: '{colors.text}' },
                muted: { value: '{colors.text.muted}' },
              },
              button: {
                primary: { value: '{colors.product_button}' },
                primaryHover: { value: '{colors.product_button.hover}' },
                primaryText: { value: '{colors.product_button.text}' },
              },
            },
          },
        },
      },
    },
    globalCss: {
      ':root': {
        '--primary-default': tokenColors?.primary?.DEFAULT?.value,
        '--primary-hover': tokenColors?.primary?.hover?.value,
        '--primary-text': tokenColors?.primary?.text?.value,
        '--bg-default': tokenColors?.background?.DEFAULT?.value,
        '--bg-card': tokenColors?.background?.card?.value,
        '--product-btn-default': tokenColors?.product_button?.DEFAULT?.value,
        '--product-btn-hover': tokenColors?.product_button?.hover?.value,
        '--product-btn-text': tokenColors?.product_button?.text?.value,
        '--text-default': tokenColors?.text?.DEFAULT?.value,
        '--text-muted': tokenColors?.text?.muted?.value,
        '--promo': tokenColors?.promo?.value,
        '--price': tokenColors?.price?.value,
        '--rate-color': '#FFB338',
        '--gray-color-300': '#F3F3F3',
        '--gray-color-400': '#EDEDED',
        '--gray-color-500': '#CDCDCD',
        '--gray-color-600': '#676767',
        '--gray-color-700': '#667085',
        '--gray-color-800': '#475467',
      },
    },
  });

  return (
    <div>
      <ChakraProvider value={system}>{children}</ChakraProvider>
    </div>
  );
};

export default ChakraUIProvider;
